import React, { useRef, useState, useEffect } from "react";
import { motion, useScroll, useTransform, useSpring, useInView } from "framer-motion";
import { Lightbulb, Calendar, Users, Building, Rocket, Mic, Quote, ChevronRight, ArrowRight, Hexagon } from "lucide-react";
import { Link } from "react-router-dom";

// --- Data ---
const museumStories = [
  {
    id: 1,
    title: "THE SPARK",
    subtitle: "GENESIS EVENT",
    content: "The inception point. A hallway conversation. A neural connection fired in the Founder's mind: 'Assistant -> AI'. The physical room dissolves into digital potential.",
    icon: <Lightbulb className="w-full h-full text-white" />,
    color: "#3B82F6", // Blue
    gridColor: "rgba(59, 130, 246, 0.4)"
  },
  {
    id: 2,
    title: "THE PITCH",
    subtitle: "DATA UPLOAD",
    content: "August 2024. The concept is visualized. The board approves the simulation. Resources allocated. The timeline is set.",
    icon: <Calendar className="w-full h-full text-white" />,
    color: "#3B82F6", // Blue
    gridColor: "rgba(59, 130, 246, 0.4)"
  },
  {
    id: 3,
    title: "INTEGRATION",
    subtitle: "SYSTEM MERGE",
    content: "FOZZIL protocol initiated. Team assembled. High-performance units synchronized. The network expands efficiently.",
    icon: <Users className="w-full h-full text-white" />,
    color: "#3B82F6", // Blue
    gridColor: "rgba(59, 130, 246, 0.4)"
  },
  {
    id: 4,
    title: "EXECUTION",
    subtitle: "BUILD PHASE",
    content: "Physical layer construction. Vendors selected. Logistics optimized. The virtual becomes tangible.",
    icon: <Rocket className="w-full h-full text-white" />,
    color: "#3B82F6", // Blue
    gridColor: "rgba(59, 130, 246, 0.4)"
  },
  {
    id: 5,
    title: "CORE SYSTEM",
    subtitle: "AI ACTIVATION",
    content: "The Brain awakens. T.A.E.L.S V1.0 goes online. Architect: The Man Behind This Dawn. The museum is now alive.",
    icon: <Mic className="w-full h-full text-white" />,
    color: "#3B82F6", // Blue
    gridColor: "rgba(59, 130, 246, 0.4)"
  }
];

const SpaceTunnel = ({ item, index }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"]
  });

  // Calculate Z-axis movement to simulate flying through a tunnel
  const z = useTransform(scrollYProgress, [0, 1], [-1000, 1000]);
  const opacity = useTransform(scrollYProgress, [0, 0.4, 0.6, 1], [0, 1, 1, 0]);
  const rotateY = useTransform(scrollYProgress, [0, 1], [-45, 45]);
  const blur = useTransform(scrollYProgress, [0, 0.5, 1], ["10px", "0px", "10px"]);
  
  const isLeft = index % 2 === 0;

  return (
    <div ref={ref} className="h-screen flex items-center justify-center relative perspective-[1000px] overflow-hidden">
       {/* Falling stars / speed lines */}
       <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <motion.div 
             style={{ x: z, opacity: 0.2 }}
             className="absolute top-0 w-[2px] h-full bg-white/20 left-[20%]"
          />
          <motion.div 
             style={{ x: useTransform(scrollYProgress, [0, 1], [1000, -1000]), opacity: 0.2 }}
             className="absolute top-0 w-[2px] h-full bg-white/20 right-[20%]"
          />
       </div>

       <motion.div 
         style={{ z, opacity, rotateY, filter: blur }}
         className={`relative z-10 max-w-6xl w-full grid grid-cols-1 md:grid-cols-2 gap-20 items-center px-6`}
       >
          {/* Visual Side */}
          <div className={`${isLeft ? 'md:order-1' : 'md:order-2'} flex justify-center`}>
             <div className="relative w-80 h-80 md:w-[35rem] md:h-[35rem] group">
                {/* 3D Portal Ring */}
                <div className="absolute inset-0 rounded-full border-4 border-white/5 animate-[spin_10s_linear_infinite]" />
                <div className="absolute inset-4 rounded-full border-2 border-dashed border-white/10 animate-[spin_15s_linear_infinite_reverse]" />
                
                {/* Central Hologram */}
                <div className="absolute inset-10 rounded-full bg-black/60 backdrop-blur-xl border border-white/10 flex items-center justify-center shadow-[0_0_100px_rgba(255,255,255,0.1)] group-hover:shadow-[0_0_150px_rgba(255,255,255,0.2)] transition-all duration-500">
                   <motion.div 
                      className="w-1/2 h-1/2" 
                      style={{ color: item.color, filter: `drop-shadow(0 0 20px ${item.color})` }}
                      animate={{ scale: [1, 1.1, 1] }} 
                      transition={{ duration: 4, repeat: Infinity }}
                   >
                     {item.icon}
                   </motion.div>
                </div>
                
                {/* Floating Particles around icon */}
                <div className="absolute top-0 left-1/2 w-2 h-2 bg-white rounded-full animate-ping" />
                <div className="absolute bottom-10 right-10 w-1 h-1 bg-white rounded-full animate-ping" />
             </div>
          </div>

          {/* Text Side */}
          <div className={`${isLeft ? 'md:order-2 text-left' : 'md:order-1 text-right'} space-y-6`}>
             <motion.div
               initial={{ x: isLeft ? 100 : -100, opacity: 0 }}
               whileInView={{ x: 0, opacity: 1 }}
               transition={{ type: "spring", stiffness: 100, damping: 20 }}
             >
                <h2 className="text-8xl md:text-[12rem] font-bold text-white/5 leading-none absolute -top-20 -z-10 select-none">
                   {index + 1}
                </h2>
                
                <h3 className="text-4xl md:text-7xl font-bold text-white leading-tight tracking-tight mb-6">
                   {item.title}
                </h3>
                
                <div className={`flex items-center gap-4 mb-8 ${!isLeft && 'flex-row-reverse'}`}>
                   <div className="h-[1px] w-20 bg-white/20" />
                   <span className="font-mono text-blue-400 tracking-[0.2em]">{item.subtitle}</span>
                </div>
                
                <p className="text-lg md:text-2xl text-gray-300 font-light leading-relaxed">
                   {item.content}
                </p>
             </motion.div>
          </div>
       </motion.div>
    </div>
  );
};

const StoriesAiMuseum = () => {
  const { scrollYProgress } = useScroll();
  const scaleX = useSpring(scrollYProgress, { stiffness: 100, damping: 30, restDelta: 0.001 });

  return (
    <div className="bg-black min-h-screen text-white font-sans selection:bg-blue-500/30 overflow-x-hidden perspective-[1px]">
      
      {/* HUD Lines */}
      <div className="fixed inset-0 pointer-events-none z-50 opacity-20 hidden md:block">
         <div className="absolute top-0 left-10 w-[1px] h-full bg-gradient-to-b from-transparent via-white to-transparent" />
         <div className="absolute top-0 right-10 w-[1px] h-full bg-gradient-to-b from-transparent via-white to-transparent" />
      </div>

      <motion.div
        className="fixed top-0 left-0 right-0 h-1 bg-gradient-to-r from-blue-500 via-indigo-500 to-purple-500 z-50 shadow-[0_0_20px_rgba(59,130,246,0.8)]"
        style={{ scaleX }}
      />

      {/* Hero Section */}
      <section className="h-screen flex flex-col items-center justify-center relative overflow-hidden perspective-[1000px]">
         <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,_var(--tw-gradient-stops))] from-blue-900/20 via-black to-black" />
         <div className="absolute inset-0 bg-[url('https://grainy-gradients.vercel.app/noise.svg')] opacity-30 mix-blend-overlay" />

         <motion.div 
            initial={{ opacity: 0, scale: 0.9, filter: "blur(20px)" }}
            animate={{ opacity: 1, scale: 1, filter: "blur(0px)" }}
            transition={{ duration: 1.5, ease: "circOut" }}
            className="z-10 text-center px-4 w-full max-w-screen-xl"
         >
            <motion.h1 
               initial={{ opacity: 0, y: 100, scale: 0.9 }}
               animate={{ opacity: 1, y: 0, scale: 1 }}
               transition={{ duration: 1.2, ease: "easeOut" }}
               className="text-[8vw] md:text-[7vw] lg:text-[8rem] font-bold tracking-tighter mb-8 text-white leading-none whitespace-nowrap"
            >
               CINTEL AI MUSEUM
            </motion.h1>
            
            <p className="text-lg md:text-2xl text-blue-200 font-light max-w-3xl mx-auto leading-relaxed">
               Where the future isn't just displayed.<br/> It's experienced.
            </p>
         </motion.div>
         
         <motion.div 
           initial={{ opacity: 0 }} 
           animate={{ opacity: 1 }}
           transition={{ delay: 2, duration: 1 }}
           className="absolute bottom-12 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 opacity-50"
         >
           <span className="text-[10px] uppercase tracking-[0.3em]">Scroll to Begin</span>
           <div className="w-[1px] h-12 bg-gradient-to-b from-white to-transparent" />
         </motion.div>
      </section>

      {/* The Tunnel Stream */}
      <div className="relative z-10">
         {museumStories.map((story, index) => (
            <SpaceTunnel key={story.id} item={story} index={index} />
         ))}
      </div>

      {/* Final Quote */}
      <section className="h-screen flex flex-col items-center justify-center relative bg-black text-center px-4 overflow-hidden">
         {/* Moving Grid Floor */}
         <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.03)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.03)_1px,transparent_1px)] bg-[size:40px_40px] [mask-image:radial-gradient(ellipse_60%_60%_at_50%_50%,#000_70%,transparent_100%)] transform perspective-[500px] rotateX(60deg) scale(2)" />
         
         <motion.div 
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            className="relative z-10 max-w-4xl p-12"
         >  
            <Quote className="w-16 h-16 text-blue-500 mx-auto mb-8" />
            
            <h2 className="text-4xl md:text-7xl font-bold italic text-white mb-8">
               "Now future triumphs future"
            </h2>
            
            <p className="text-gray-400 font-mono text-sm mb-12">
               {"// INTELLIGENCE_AT_YOUR_COMMAND!"}
            </p>
         </motion.div>
      </section>

    </div>
  );
};

export default StoriesAiMuseum;
